import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
const initialState = {
    conversations: [],
    currentConversation: null,
    messages: [],
    status: 'idle',
    error: null,
};
// Async thunks
export const fetchConversations = createAsyncThunk('messages/fetchConversations', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/conversations');
        return response.data.data;
    }
    catch (error) {
        if (error.response?.status === 401) {
            // Handle authentication errors
            console.error('Authentication error fetching conversations');
        }
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch conversations');
    }
});
export const fetchConversationMessages = createAsyncThunk('messages/fetchConversationMessages', async ({ userId, recipientId }, { rejectWithValue }) => {
    try {
        // Use the correct API pattern for getting messages between two users
        const response = await axiosInstance.get(`/conversations/${recipientId}/messages`);
        return response.data.data;
    }
    catch (error) {
        if (error.response?.status === 401) {
            console.error('Authentication error fetching conversation messages');
        }
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch messages');
    }
});
export const sendMessage = createAsyncThunk('messages/sendMessage', async (messageData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/messages', messageData);
        return response.data.data;
    }
    catch (error) {
        if (error.response?.status === 401) {
            console.error('Authentication error sending message');
        }
        return rejectWithValue(error.response?.data?.message || 'Failed to send message');
    }
});
// Slice
const messageSlice = createSlice({
    name: 'messages',
    initialState,
    reducers: {
        setCurrentConversation: (state, action) => {
            state.currentConversation = action.payload;
        },
        addMessage: (state, action) => {
            state.messages.push(action.payload);
        },
        updateMessageStatus: (state, action) => {
            const message = state.messages.find(msg => msg.id === action.payload.messageId);
            if (message) {
                message.status = action.payload.status;
            }
        },
        clearMessages: (state) => {
            state.messages = [];
        },
        updateConversationLastMessage: (state, action) => {
            const conversation = state.conversations.find(conv => conv.id === action.payload.conversationId);
            if (conversation && conversation.last_message) {
                conversation.last_message.content = action.payload.lastMessage;
                conversation.last_message.sent_at = action.payload.lastMessageTime;
                // Move conversation to top
                state.conversations = [
                    conversation,
                    ...state.conversations.filter(conv => conv.id !== action.payload.conversationId)
                ];
            }
        },
        incrementUnreadCount: (state, action) => {
            const conversation = state.conversations.find(conv => conv.id === action.payload);
            if (conversation) {
                conversation.unread_count++;
            }
        },
        clearUnreadCount: (state, action) => {
            const conversation = state.conversations.find(conv => conv.id === action.payload);
            if (conversation) {
                conversation.unread_count = 0;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch conversations
            .addCase(fetchConversations.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(fetchConversations.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.conversations = action.payload;
            state.error = null;
        })
            .addCase(fetchConversations.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            // Fetch conversation messages
            .addCase(fetchConversationMessages.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(fetchConversationMessages.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.messages = action.payload;
            state.error = null;
        })
            .addCase(fetchConversationMessages.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        })
            // Send message
            .addCase(sendMessage.fulfilled, (state, action) => {
            state.messages.push(action.payload);
        });
    },
});
export const { setCurrentConversation, addMessage, updateMessageStatus, clearMessages, updateConversationLastMessage, incrementUnreadCount, clearUnreadCount, } = messageSlice.actions;
export default messageSlice.reducer;
